// Counting (spec/13 §6.4 Count): a count on its own page, because counting forty items on a tablet is a job, not a
// dialog — big inputs, no nested scroll, a link that can be reopened. The sheet lists the count's items by kind; each
// row takes packs and loose units ("2.5 kg bag": 1, loose: 0.45 kg) and is kept on the server as soon as it is
// entered (`PUT …/counts/{id}/lines`) — Enter saves the row and moves to the next, leaving a field saves it too — so a
// closed tab or a second person counting the other shelf loses nothing. The sheet is **blind**: it does not show what
// the system expects, so a count is a count, not a confirmation.
//
// A row reopens on what was entered — the packs and the loose units — not on the total they made.
//
// **Review** then shows each item's expected quantity (what was on hand when it was counted, so a sale between the
// count and the post is not a difference), what was counted and the difference, in units and — with
// `inventory:reports` — in rupees, biggest first, with the net. **Post count** (after a confirm that says what it
// does, and takes an optional note on what was found) makes every difference a `count` movement; a full count with an
// item still to count is refused, and the review names them first. **Cancel count** discards it. A posted or cancelled
// count opens read-only on its review.

import type { StockCount, StockCountLine, StockItem } from '@clavian/api-client';
import { useTranslation, useVenueFormatter } from '@clavian/i18n/react';
import { Badge, Button, Dialog, ProgressBar, SegmentedControl, StatCard, Table, TextField, useToast, type TableColumn } from '@clavian/ui-web';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { useRef, useState, type KeyboardEvent, type ReactNode } from 'react';

import { PROBLEM, problemCode, problemDetail } from '../../api/problems';
import { useProblem } from '../../api/use-problem';
import { LoadFailure } from '../../shell/LoadFailure';
import { Icon } from '../../shell/nav-icons';
import { useCurrentVenue } from '../../shell/scope';
import { Forbidden, Notice, Page, SearchField, Section, SummaryStrip, TableSkeleton, Toolbar } from '../../ui';
import { TickingNumber } from '../../ui/ticking';
import { useStockAccess } from './access';
import { useStockFormat, useStockUnits } from './format';
import { entryOf, entryToWire, entryTotal, QuantityFields, type QuantityEntry } from './parts';
import { useCancelCount, usePutCountLines, useStockCount, useStockItems, useSubmitCount } from './queries';
import { useWhen } from './StockPage';

import './stock.css';

type Saving = 'saving' | 'saved' | 'error';
type Show = 'all' | 'todo' | 'counted';
const KIND_ORDER = ['ingredient', 'retail', 'supply'] as const;

export function CountPage(): ReactNode {
  const { t } = useTranslation('console');
  const venue = useCurrentVenue();
  const { countId } = useParams({ strict: false }) as { readonly countId: string };
  const access = useStockAccess(venue.id);
  const count = useStockCount(venue.organizationId, venue.id, countId);
  const items = useStockItems(venue.organizationId, venue.id);
  const [reviewing, setReviewing] = useState(false);
  const when = useWhen();

  const back = (
    <Link to="/v/$venueSlug/stock/$tab" params={{ venueSlug: venue.slug, tab: 'counts' }} className="console-stock-back">
      <Icon name="prev" /> {t('stock.count.back')}
    </Link>
  );

  if (count.data === undefined || items.data === undefined) {
    const failed = count.isError ? count.error : items.isError ? items.error : null;
    return (
      <Page title={t('stock.count.loadingTitle')} eyebrow={back}>
        {failed === null ? (
          <TableSkeleton rows={8} label={t('stock.loading')} />
        ) : problemCode(failed) === PROBLEM.forbidden ? (
          <Forbidden message={t('stock.notPermitted')} />
        ) : (
          <LoadFailure error={failed} message={t('stock.count.unavailable')} onRetry={() => void count.refetch()} />
        )}
      </Page>
    );
  }

  const data = count.data;
  const open = data.status === 'open';
  const canCount = access.count;
  const byId = new Map(items.data.map((item) => [item.id, item]));
  const showReview = !open || reviewing || !canCount;
  const subtitle = [
    open ? t('stock.counts.startedBy', { when: when(data.startedAt), who: data.startedByName ?? t('stock.history.system') }) : null,
    data.status === 'submitted' && data.submittedAt !== null ? t('stock.count.postedBy', { when: when(data.submittedAt), who: data.submittedByName ?? t('stock.history.system') }) : null,
    data.status === 'cancelled' && data.cancelledAt !== null ? t('stock.count.cancelledAt', { when: when(data.cancelledAt) }) : null,
    data.note,
  ]
    .filter((part): part is string => part !== null && part !== '')
    .join(' · ');

  return (
    <Page
      eyebrow={back}
      title={t(`stock.counts.kind.${data.kind}`)}
      description={subtitle}
      meta={<Badge tone={open ? 'accent' : data.status === 'submitted' ? 'ok' : 'neutral'}>{t(`stock.counts.statusWord.${data.status}`)}</Badge>}
      width="wide"
      actions={
        open && canCount && !reviewing ? (
          <Button variant="primary" icon={<Icon name="check" />} onClick={() => setReviewing(true)}>
            {t('stock.count.review')}
          </Button>
        ) : undefined
      }
    >
      <Progress count={data} />
      {showReview ? (
        <Review count={data} reports={access.reports} canPost={open && canCount} onBack={() => setReviewing(false)} />
      ) : (
        <Sheet count={data} byId={byId} onReview={() => setReviewing(true)} />
      )}
    </Page>
  );
}

/** How far the count has got: a bar and "12 of 40 counted". */
function Progress({ count }: { readonly count: StockCount }): ReactNode {
  const { t } = useTranslation('console');
  const label = t('stock.counts.progressOf', { counted: count.countedCount, total: count.lineCount });
  return (
    <div className="console-stock-progress">
      <ProgressBar value={count.lineCount === 0 ? 0 : count.countedCount / count.lineCount} label={label} tone={count.countedCount === count.lineCount ? 'ok' : 'accent'} />
      <span className="console-num console-stock-progress__words">{label}</span>
    </div>
  );
}

// -------------------------------------------------------------------------------------------------
// The counting sheet
// -------------------------------------------------------------------------------------------------

function Sheet({ count, byId, onReview }: { readonly count: StockCount; readonly byId: ReadonlyMap<string, StockItem>; readonly onReview: () => void }): ReactNode {
  const { t } = useTranslation('console');
  const venue = useCurrentVenue();
  const put = usePutCountLines(venue.organizationId, venue.id, count.id);
  const [query, setQuery] = useState('');
  const [show, setShow] = useState<Show>('all');
  // What has been typed here, by item; a row nobody has touched here follows the server (a second counter).
  const [drafts, setDrafts] = useState<Readonly<Record<string, QuantityEntry>>>({});
  const [saving, setSaving] = useState<Readonly<Record<string, Saving>>>({});
  const firstInputs = useRef(new Map<string, HTMLInputElement | null>());
  // What was last sent for each row, so Enter and the blur that follows it send a figure once.
  const lastSent = useRef(new Map<string, number | null>());

  const lines = count.lines.filter((line) => byId.has(line.stockItemId));
  const needle = query.trim().toLowerCase();
  const visible = lines
    .filter((line) => needle === '' || line.name.toLowerCase().includes(needle))
    .filter((line) => show === 'all' || (show === 'todo' ? line.counted === null : line.counted !== null));
  const groups = KIND_ORDER.map((kind) => ({ kind, lines: visible.filter((line) => byId.get(line.stockItemId)?.kind === kind) })).filter((group) => group.lines.length > 0);
  const order = groups.flatMap((group) => group.lines.map((line) => line.stockItemId));

  const entryFor = (line: StockCountLine, item: StockItem): QuantityEntry => drafts[line.stockItemId] ?? entryOf(item, line.counted, line.entered);

  /** Keep a row on the server: its figure, or — emptied — forget it. Nothing is sent when nothing changed. */
  function commit(line: StockCountLine, item: StockItem): void {
    const entry = drafts[line.stockItemId];
    if (entry === undefined) return;
    const total = entryTotal(entry, item);
    if (total === 'invalid') return;
    if (total === line.counted) return;
    if (total === null && line.counted === null) return;
    if (lastSent.current.has(line.stockItemId) && lastSent.current.get(line.stockItemId) === total) return;
    lastSent.current.set(line.stockItemId, total);
    setSaving((current) => ({ ...current, [line.stockItemId]: 'saving' }));
    put.mutate(
      { lines: [total === null ? { stockItemId: line.stockItemId, clear: true } : { stockItemId: line.stockItemId, ...entryToWire(entry, item) }] },
      {
        onSuccess: () => setSaving((current) => ({ ...current, [line.stockItemId]: 'saved' })),
        onError: () => {
          // Not saved: the next Enter or blur tries again.
          lastSent.current.delete(line.stockItemId);
          setSaving((current) => ({ ...current, [line.stockItemId]: 'error' }));
        },
      },
    );
  }

  /** Enter saves the row and moves to the next one, so a count runs down the shelf without the mouse. */
  const onKeyDown = (line: StockCountLine, item: StockItem) => (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    commit(line, item);
    const next = order[order.indexOf(line.stockItemId) + 1];
    if (next !== undefined) firstInputs.current.get(next)?.focus();
  };

  const todo = lines.filter((line) => line.counted === null).length;
  return (
    <div className="console-stock-sheet">
      <Toolbar label={t('stock.count.toolbar')} className="console-stock-toolbar">
        <SearchField label={t('stock.count.search')} value={query} onChange={(event) => setQuery(event.target.value)} />
        <SegmentedControl<Show>
          label={t('stock.count.show')}
          size="sm"
          value={show}
          onValueChange={setShow}
          options={[
            { value: 'all', label: t('stock.count.showAll'), count: lines.length },
            { value: 'todo', label: t('stock.count.showTodo'), count: todo },
            { value: 'counted', label: t('stock.count.showCounted'), count: lines.length - todo },
          ]}
        />
      </Toolbar>
      <p className="console-subtle console-stock-sheet__hint">{t('stock.count.blindHint')}</p>
      {groups.length === 0 ? (
        <p className="console-stock-hint">{needle === '' ? t('stock.count.nothingHere') : t('common.noMatches', { query: query.trim() })}</p>
      ) : (
        groups.map((group) => (
          <Section key={group.kind} title={t(`stock.kindPlural.${group.kind}`)} description={t('stock.count.groupCount', { count: group.lines.length })} body="rows">
            <ul className="console-stock-sheet__rows">
              {group.lines.map((line) => {
                const item = byId.get(line.stockItemId);
                if (item === undefined) return null;
                const entry = entryFor(line, item);
                const state = saving[line.stockItemId];
                return (
                  <li key={line.stockItemId} className={line.counted === null ? 'console-stock-sheet__row' : 'console-stock-sheet__row is-counted'}>
                    <div className="console-stock-sheet__name">
                      <span className="console-stock-sheet__item">{item.name}</span>
                    </div>
                    <QuantityFields
                      compact
                      item={item}
                      entry={entry}
                      label={item.name}
                      onChange={(next) => {
                        setDrafts((current) => ({ ...current, [line.stockItemId]: next }));
                        setSaving((current) => {
                          const rest = { ...current };
                          delete rest[line.stockItemId];
                          return rest;
                        });
                      }}
                      onKeyDown={onKeyDown(line, item)}
                      onBlur={() => commit(line, item)}
                      firstInputRef={(element) => {
                        firstInputs.current.set(line.stockItemId, element);
                      }}
                    />
                    <RowState line={line} state={state} item={item} />
                  </li>
                );
              })}
            </ul>
          </Section>
        ))
      )}
      <div className="console-stock-sheet__bar">
        <span className="console-num">{t('stock.count.left', { count: todo })}</span>
        <Button variant="primary" icon={<Icon name="check" />} onClick={onReview}>
          {t('stock.count.review')}
        </Button>
      </div>
    </div>
  );
}

/** A row's state in words: counted (what), saving, not saved, or still to count. */
function RowState({ line, state, item }: { readonly line: StockCountLine; readonly state: Saving | undefined; readonly item: StockItem }): ReactNode {
  const { t } = useTranslation('console');
  const stock = useStockFormat();
  if (state === 'saving') return <span className="console-stock-sheet__state console-subtle" role="status">{t('stock.count.saving')}</span>;
  if (state === 'error')
    return (
      <span className="console-stock-sheet__state console-stock-sheet__state--error" role="alert">
        {t('stock.count.notSaved')}
      </span>
    );
  if (line.counted === null) return <span className="console-stock-sheet__state console-subtle">{t('stock.count.toCount')}</span>;
  return (
    <span className="console-stock-sheet__state console-stock-sheet__state--done" role={state === 'saved' ? 'status' : undefined}>
      <Icon name="check" /> <span className="console-num">{stock.quantity(line.counted, item)}</span>
    </span>
  );
}

// -------------------------------------------------------------------------------------------------
// The review
// -------------------------------------------------------------------------------------------------

function Review({
  count,
  reports,
  canPost,
  onBack,
}: {
  readonly count: StockCount;
  readonly reports: boolean;
  readonly canPost: boolean;
  readonly onBack: () => void;
}): ReactNode {
  const { t } = useTranslation('console');
  const problem = useProblem();
  const { show } = useToast();
  const venue = useCurrentVenue();
  const navigate = useNavigate();
  const format = useVenueFormatter();
  const stock = useStockFormat();
  const units = useStockUnits();
  const submit = useSubmitCount(venue.organizationId, venue.id, count.id);
  const cancel = useCancelCount(venue.organizationId, venue.id, count.id);
  const [confirming, setConfirming] = useState<'post' | 'cancel' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState('');
  // One key per post: kept while the same post is retried (a dropped answer replays rather than posts twice), new once
  // the note changes, because the same key with another body is refused.
  const attempt = useRef<{ note: string; key: string } | null>(null);

  const open = count.status === 'open';
  const counted = count.lines.filter((line) => line.counted !== null);
  const uncounted = count.lines.filter((line) => line.counted === null);
  const differing = counted.filter((line) => (line.variance ?? 0) !== 0);
  const matching = counted.length - differing.length;
  const blocked = open && count.kind === 'full' && uncounted.length > 0;
  const rows = [...differing].sort((a, b) => Math.abs(b.varianceValue?.amount ?? 0) - Math.abs(a.varianceValue?.amount ?? 0) || Math.abs(b.variance ?? 0) - Math.abs(a.variance ?? 0));
  const net = count.varianceValue;
  const currency = venue.currency ?? 'INR';
  const whole = (value: number) => format.number(value);
  const lossTotal = differing.reduce((sum, line) => sum + Math.min(0, line.varianceValue?.amount ?? 0), 0);
  const gainTotal = differing.reduce((sum, line) => sum + Math.max(0, line.varianceValue?.amount ?? 0), 0);

  const columns: TableColumn<StockCountLine>[] = [
    { key: 'item', header: t('stock.column.item'), cell: (line) => line.name },
    { key: 'expected', header: t('stock.count.expected'), numeric: true, cell: (line) => <span className="console-num console-muted">{stock.quantity(line.expected, units(line.stockItemId, line.baseUnit))}</span> },
    { key: 'counted', header: t('stock.count.counted'), numeric: true, cell: (line) => <span className="console-num">{line.counted === null ? t('stock.count.toCount') : stock.quantity(line.counted, units(line.stockItemId, line.baseUnit))}</span> },
    {
      key: 'variance',
      header: t('stock.count.difference'),
      numeric: true,
      cell: (line) => <span className={(line.variance ?? 0) < 0 ? 'console-num console-stock-change--out' : 'console-num console-stock-change--in'}>{line.variance === null ? '' : stock.change(line.variance, units(line.stockItemId, line.baseUnit))}</span>,
    },
    ...(reports
      ? [
          {
            key: 'value',
            header: t('stock.count.differenceValue'),
            numeric: true,
            cell: (line: StockCountLine) => <span className={(line.varianceValue?.amount ?? 0) < 0 ? 'console-money console-stock-change--out' : 'console-money'}>{signed(stock.money(line.varianceValue), line.varianceValue?.amount ?? 0)}</span>,
          },
        ]
      : []),
  ];

  function post(): void {
    const said = note.trim();
    if (attempt.current?.note !== said) attempt.current = { note: said, key: crypto.randomUUID() };
    submit.mutate({ key: attempt.current.key, ...(said === '' ? {} : { note: said }) }, {
      onSuccess: (posted) => {
        setConfirming(null);
        show({ tone: 'ok', title: t('stock.count.posted', { count: posted.lineCount }) });
      },
      onError: (failure) => {
        setConfirming(null);
        // The shared copy says which counts are missing; the screen's own line (with the number still to count) stands in
        // for that one, and `postRefused` for a code with no copy.
        setError(
          problemCode(failure) === PROBLEM.stockCountIncomplete
            ? (problemDetail(failure) ?? t('stock.count.incomplete', { count: uncounted.length }))
            : problem.text(failure, { fallback: t('stock.count.postRefused') }),
        );
      },
    });
  }

  function discard(): void {
    cancel.mutate(undefined, {
      onSuccess: () => {
        setConfirming(null);
        show({ tone: 'ok', title: t('stock.count.cancelled') });
        void navigate({ to: '/v/$venueSlug/stock/$tab', params: { venueSlug: venue.slug, tab: 'counts' } });
      },
      onError: (failure) => {
        setConfirming(null);
        setError(problem.text(failure, { fallback: t('stock.count.cancelRefused') }));
      },
    });
  }

  return (
    <div className="console-stock-review">
      {error !== null ? (
        <Notice tone="danger" role="alert">
          {error}
        </Notice>
      ) : null}
      {count.submitNote === null ? null : (
        <Notice role="note" title={t('stock.count.postedNote')}>
          <p>{count.submitNote}</p>
        </Notice>
      )}
      {blocked ? (
        <Notice tone="warn" title={t('stock.count.uncountedTitle', { count: uncounted.length })} action={canPost ? <Button size="sm" variant="secondary" onClick={onBack}>{t('stock.count.backToCounting')}</Button> : undefined}>
          <p>
            {t('stock.count.uncountedBody', {
              names: [uncounted.slice(0, 6).map((line) => line.name).join(', '), uncounted.length > 6 ? t('stock.count.andMore', { count: uncounted.length - 6 }) : '']
                .filter((part) => part !== '')
                .join(' '),
            })}
          </p>
        </Notice>
      ) : null}
      <SummaryStrip label={t('stock.count.summary')}>
        <StatCard
          className="console-summary__cell--live"
          label={t('stock.count.countedFigure')}
          value={<TickingNumber className="console-num" value={counted.length} format={whole} />}
          hint={t('stock.count.ofItems', { count: count.lineCount })}
        />
        <StatCard
          label={t('stock.count.differentFigure')}
          value={<TickingNumber className="console-num" value={differing.length} format={whole} />}
          hint={t('stock.count.matching', { count: matching })}
          tone={differing.length > 0 ? 'warn' : 'default'}
        />
        {reports ? (
          <StatCard
            label={open ? t('stock.count.netPreview') : t('stock.count.net')}
            value={<TickingNumber className="console-money" value={net?.amount ?? 0} format={(amount) => signed(stock.money({ amount, currency }), amount)} />}
            hint={t('stock.count.lossGain', { loss: stock.money({ amount: Math.abs(lossTotal), currency }), gain: stock.money({ amount: gainTotal, currency }) })}
            tone={(net?.amount ?? 0) < 0 ? 'danger' : 'default'}
          />
        ) : null}
      </SummaryStrip>
      <Section title={t('stock.count.differences')} description={differing.length === 0 ? t('stock.count.noDifferences') : t('stock.count.differencesHint')} body="rows">
        <Table caption={t('stock.count.reviewCaption')} captionHidden columns={columns} rows={rows} rowKey={(line) => line.stockItemId} empty={t('stock.count.noDifferences')} />
      </Section>
      {open && canPost ? (
        <div className="console-stock-sheet__bar">
          <Button variant="ghost" onClick={() => setConfirming('cancel')}>
            {t('stock.count.cancel')}
          </Button>
          <span className="console-toolbar__spring" aria-hidden="true" />
          <Button variant="secondary" onClick={onBack}>
            {t('stock.count.backToCounting')}
          </Button>
          <Button variant="primary" disabled={blocked} onClick={() => setConfirming('post')}>
            {t('stock.count.post')}
          </Button>
        </div>
      ) : null}
      <Dialog
        open={confirming === 'post'}
        onOpenChange={(next) => (next ? undefined : setConfirming(null))}
        title={t('stock.count.postTitle')}
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirming(null)}>
              {t('stock.count.keepReviewing')}
            </Button>
            <Button variant="primary" busy={submit.isPending} onClick={post}>
              {t('stock.count.post')}
            </Button>
          </>
        }
      >
        <div className="console-form">
          <p>{t('stock.count.postBody', { count: counted.length })}</p>
          {reports && net !== null ? <p className="console-num">{t('stock.count.postNet', { amount: signed(stock.money(net), net.amount) })}</p> : null}
          <TextField label={t('stock.count.note')} showOptional autoComplete="off" maxLength={500} value={note} placeholder={t('stock.count.notePlaceholder')} onChange={(event) => setNote(event.target.value)} />
          <p className="console-subtle">{t('stock.count.postFinal')}</p>
        </div>
      </Dialog>
      <Dialog
        open={confirming === 'cancel'}
        onOpenChange={(next) => (next ? undefined : setConfirming(null))}
        title={t('stock.count.cancelTitle')}
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirming(null)}>
              {t('stock.count.keepCount')}
            </Button>
            <Button variant="danger" busy={cancel.isPending} onClick={discard}>
              {t('stock.count.cancel')}
            </Button>
          </>
        }
      >
        <p>{t('stock.count.cancelBody', { count: counted.length })}</p>
      </Dialog>
    </div>
  );
}

/** A money figure with its sign spelled out: "−₹21.60", "+₹4.50". */
function signed(text: string, amount: number): string {
  return amount > 0 ? `+${text}` : text;
}
